/**
 * Board -> app path mapping for 1:1 canvas boards. Main-bundle safe (registry only); the compiled
 * board loaders live in ./loaders (lazy BoardView chunk).
 */
import { DOC_BOARDS, screens, webPath, webScreens, type Screen } from '@screens';

const known = new Set(screens.map((s) => s.board));

/** Every registry board is compiled from design/canvas by scripts/canvas/compile-boards.mjs. */
export function hasDesign(board: string): boolean {
  return known.has(board);
}

/** Same screen set and order as canvas/routes.tsx canvasRoutes() + canvasFullscreenRoutes(). */
const routed: Screen[] = [...webScreens, ...screens.filter((s) => DOC_BOARDS.has(s.board) && s.device !== 'phone')];
const owner = new Map<string, string>();
for (const s of routed) {
  const p = webPath(s);
  if (!owner.has(p)) owner.set(p, s.board);
}
for (const s of screens) {
  if (s.device !== 'tv') continue;
  const p = webPath(s);
  if (!owner.has(p)) owner.set(p, s.board);
}

export function designPath(board: string): string {
  return `/design/${encodeURIComponent(board)}`;
}

/**
 * The app path a board link navigates to: the working page or canvas route that serves the board,
 * or /design/<board> when no route serves it (phone boards, boards sharing a route).
 */
export function boardPath(board: string): string | null {
  const s = screens.find((x) => x.board === board);
  if (!s) return null;
  if (s.device === 'phone') return designPath(board);
  const p = webPath(s);
  if (s.wiredWeb && s.wiredWeb === p) return p;
  return owner.get(p) === board ? p : designPath(board);
}
